// src/pages/Lawyer/LawyerPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Spinner, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import PaginatedTable from "../Common/PaginatedTable";
import Unauthorized from "../../components/Common/Unauthorized";
import usePermission from "../../hooks/usePermission";
import {
  getLawyersPaged,
  deleteLawyer,
  updateLawyer,
  getLawyerById,
} from "../../services/lawyerService";
import {
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  UserCog,
  Eye,
  GraduationCap,
} from "lucide-react";
import LawyerDetailsModal from "../../components/Lawyer/LawyerDetailsModal";
import extractApiErrors from "../../utils/extractApiErrors";
const STUDENT_CODE = "ESTUDIANTE";

// helper: devuelve null si viene vacío/whitespace
const toNullIfEmpty = (v) => {
  if (v === undefined || v === null) return null;
  if (typeof v === "string" && v.trim() === "") return null;
  return v;
};

const normalize = (v) =>
  String(v ?? "")
    .trim()
    .toUpperCase();

// ✅ Mejor: que el backend devuelva academicLevelCode o isStudent
const isStudentRow = (row) => {
  if (row?.isStudent === true) return true;
  return normalize(row?.academicLevelCode) === STUDENT_CODE;
};

const LawyerPage = () => {
  const navigate = useNavigate();
  const hasPermission = usePermission();

  const [lawyers, setLawyers] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedLawyerId, setSelectedLawyerId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchLawyers = useCallback(async (page, size, search = "") => {
    try {
      setLoading(true);
      const response = await getLawyersPaged(page, size, search);
      if (response?.success) {
        setLawyers(response.data.items || []);
        setTotalRows(response.data.totalCount || 0);
      } else {
        throw new Error(response?.message || "Error al cargar abogados");
      }
    } catch {
      Swal.fire("Error", "No se pudieron cargar los abogados", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLawyers(currentPage, perPage, searchTerm);
  }, [fetchLawyers, currentPage, perPage, searchTerm]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleCreate = () => navigate("/dashboard/lawyers/create");
  const handleEdit = (id) => navigate(`/dashboard/lawyers/edit/${id}`);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará el abogado.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await deleteLawyer(id);

      // Caso: el service retorna JSON con success=false (sin throw)
      if (!response?.success) {
        const msg = response?.message || "No se pudo eliminar el abogado.";
        await Swal.fire("No se puede eliminar", msg, "warning");
        return;
      }

      await Swal.fire(
        "Eliminado",
        "El abogado fue eliminado correctamente.",
        "success"
      );
      fetchLawyers(currentPage, perPage, searchTerm);
    } catch (error) {
      // Caso: Axios throw -> usar extractor
      const errors = extractApiErrors(error);
      await Swal.fire({
        title: "No se puede eliminar",
        icon: "warning",
        html: `<ul style="text-align:left;margin:0;padding-left:18px;">
        ${errors.map((e) => `<li>${e}</li>`).join("")}
      </ul>`,
      });
    }
  };

  // Activar / Inactivar
  const handleToggleActive = async (row) => {
    const nextState = !row.isActive;

    const result = await Swal.fire({
      title: nextState
        ? "¿Deseas activar este abogado?"
        : "¿Deseas inactivar este abogado?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, confirmar",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;

    try {
      const detailResp = await getLawyerById(row.id);
      if (!detailResp?.success)
        throw new Error("No se pudo obtener el detalle");

      const d = detailResp.data || {};

      // Payload update consistente con backend (camelCase y nulls)
      const payload = {
        id: d.id,

        securityUser: {
          id: d.securityUserId,
          companyId: d.companyId ?? undefined,
          firstName: d.firstName ?? "",
          lastName: d.lastName ?? "",
          email: d.email ?? "",
          isActive: nextState,
          password: undefined,
          confirmPassword: undefined,
          userType: 3, // Lawyer
          roleIds: Array.isArray(d.roleIds) ? d.roleIds : [],
        },

        documentTypeId: d.documentTypeId ?? null,
        documentNumber: d.documentNumber ?? "",
        birthDate: d.birthDate ?? null,
        genderId: d.genderId ?? null,
        isActive: nextState,
        phoneMobile: d.phoneMobile ?? "",
        phoneAlt: toNullIfEmpty(d.phoneAlt),

        addressLine1: d.addressLine1 ?? "",
        addressLine2: toNullIfEmpty(d.addressLine2),

        countryId: d.countryId ?? null,
        departmentId: d.departmentId ?? null,
        municipalityId: d.municipalityId ?? null,

        academicLevelId: d.academicLevelId ?? null,
        mainDegreeTitle: toNullIfEmpty(d.mainDegreeTitle),
        mainInstitution: toNullIfEmpty(d.mainInstitution),
        mainGraduationDate: d.mainGraduationDate ?? null,

        // Licencia puede ser null si estudiante
        professionalLicenseNumber: toNullIfEmpty(d.professionalLicenseNumber),
        professionalLicenseIssueDate: d.professionalLicenseIssueDate ?? null,
        professionalLicenseIssuedBy: toNullIfEmpty(
          d.professionalLicenseIssuedBy
        ),
        isLicenseVerified: !!d.isLicenseVerified,

        summary: toNullIfEmpty(d.summary),

        specialtyIds: Array.isArray(d.specialtyIds) ? d.specialtyIds : [],

        // Tutor (nuevo)
        tutorLawyerId: d.tutorLawyerId ?? null,

        educations: Array.isArray(d.educations)
          ? d.educations.map((e) => ({
              id: e?.id ?? undefined,
              titleName: e?.titleName ?? "",
              institution: e?.institution ?? "",
              academicLevelId: e?.academicLevelId ?? null,
              startDate: e?.startDate ?? null,
              graduationDate: e?.graduationDate ?? null,
              isGraduated: !!e?.isGraduated,
            }))
          : [],
      };

      const resp = await updateLawyer(row.id, payload);
      if (!resp?.success) throw new Error(resp?.message || "Error");

      Swal.fire("Éxito", "Estado actualizado correctamente.", "success");
      fetchLawyers(currentPage, perPage, searchTerm);
    } catch {
      Swal.fire("Error", "No se pudo actualizar el estado.", "error");
    }
  };

  const handleViewAllData = async (id) => {
    setSelectedLawyerId(id);
    setShowDetailsModal(true);
    setDetailsData(null);
    setDetailsLoading(true);

    try {
      const response = await getLawyerById(id);
      if (response?.success) setDetailsData(response.data);
      else throw new Error();
    } catch {
      Swal.fire("Error", "No se pudo cargar el detalle del abogado.", "error");
      setShowDetailsModal(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  if (!hasPermission("abogados.view")) return <Unauthorized />;

  const columns = [
    {
      name: "Nombre",
      sortable: true,
      wrap: true,
      width: "260px",
      // ⚡ Render más informativo: badge ESTUDIANTE + tutor (si viene)
      cell: (row) => {
        const student = isStudentRow(row);
        return (
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-semibold">{row.fullName}</span>

              {student && (
                <Badge
                  bg="success"
                  className="d-inline-flex align-items-center gap-1 text-uppercase"
                  title="Abogado estudiante (Laboratorio jurídico)"
                >
                  <GraduationCap size={14} />
                  Estudiante
                </Badge>
              )}
            </div>

            {/* Opcional: si tu paged DTO incluye tutorLawyerName */}
            {student && row.tutorLawyerName && (
              <small className="text-muted">Tutor: {row.tutorLawyerName}</small>
            )}
          </div>
        );
      },
      // Para sorting consistente (porque cell es JSX)
      selector: (row) => row.fullName,
    },
    {
      name: "Documento",
      selector: (row) => row.documentNumber,
      sortable: true,
      wrap: true,
      width: "150px",
    },
    {
      name: "Licencia",
      sortable: true,
      wrap: true,
      width: "170px",
      selector: (row) => row.professionalLicenseNumber || "",
      cell: (row) => {
        const student = isStudentRow(row);
        if (student) return <span className="text-muted">No aplica</span>;
        return row.professionalLicenseNumber
          ? row.professionalLicenseNumber
          : "—";
      },
    },
    {
      name: "Especialidades",
      selector: (row) =>
        Array.isArray(row.specialtyNames) && row.specialtyNames.length
          ? row.specialtyNames.join(", ")
          : "—",
      wrap: true,
      grow: 2,
    },
    {
      name: "Activo",
      center: true,
      width: "150px",
      cell: (row) =>
        hasPermission("abogados.update") ? (
          <Button
            variant="light"
            size="sm"
            className="d-flex align-items-center gap-1 border-0"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActive(row);
            }}
          >
            {row.isActive ? (
              <>
                <CheckCircle size={16} className="text-success" /> Activo
              </>
            ) : (
              <>
                <XCircle size={16} className="text-danger" /> Inactivo
              </>
            )}
          </Button>
        ) : row.isActive ? (
          <span className="text-success fw-semibold">Activo</span>
        ) : (
          <span className="text-danger fw-semibold">Inactivo</span>
        ),
    },
    {
      name: "Acciones",
      center: true,
      width: "210px",
      cell: (row) => (
        <div className="d-flex gap-2">
          {hasPermission("abogados.update") && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.id);
              }}
              title="Editar"
            >
              <Pencil size={16} />
            </Button>
          )}

          {hasPermission("abogados.delete") && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.id);
              }}
              title="Eliminar"
            >
              <Trash2 size={16} />
            </Button>
          )}

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewAllData(row.id);
            }}
            title="Ver todos los datos"
          >
            <Eye size={16} className="me-1" /> Ver datos
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <UserCog size={22} />
            <h4 className="mb-0 text-dark">Abogados registrados</h4>
          </div>

          {hasPermission("abogados.create") && (
            <Button onClick={handleCreate}>+ Crear abogado</Button>
          )}
        </div>

        <PaginatedTable
          keyField="id"
          columns={columns}
          data={lawyers}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePerRowsChange}
          onSearch={handleSearch}
          progressPending={loading}
        />

        {loading && (
          <div className="d-flex justify-content-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="text-muted">Cargando...</span>
          </div>
        )}
      </Card>

      <LawyerDetailsModal
        visible={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        data={detailsData}
        loading={detailsLoading}
        onReload={() =>
          getLawyerById(selectedLawyerId).then((r) => {
            if (r?.success) setDetailsData(r.data);
          })
        }
      />
    </div>
  );
};

export default LawyerPage;
